/**
 * Whether the board has been asked to load.
 *
 * Every query is `enabled: loaded`, so an open tab costs nothing until someone
 * presses Load. Once loaded, results are held for a day both here and in the
 * server-side Redis cache, so a reload inside the window is free.
 *
 * The timestamp is kept so the header can state when the data was pulled. A
 * report that does not say how old it is invites being read as current — the
 * same failure the snapshot panels already guard against.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'

type Ctx = {
  loaded: boolean
  loadedAt: Date | null
  load: () => void
  /** Discard everything and fetch again, ignoring the day-long client cache. */
  refresh: () => void
}

const LoadCtx = createContext<Ctx | null>(null)

export function LoadProvider(
  { children, initialLoadedAt = null }:
  { children: ReactNode; initialLoadedAt?: Date | null },
) {
  const qc = useQueryClient()
  // `initialLoadedAt` is a testing seam: panel tests assert what a LOADED board
  // renders, and making each of them click the button first would test the button
  // 30 times and the panels once.
  const [loadedAt, setLoadedAt] = useState<Date | null>(initialLoadedAt)

  const load = useCallback(() => {
    setLoadedAt((prev) => prev ?? new Date())
  }, [])

  const refresh = useCallback(() => {
    // Clear the SERVER cache first. Dropping only the client cache would send a
    // fresh request that Redis answers from the same day-old payload, so Refresh
    // would appear to do nothing. Best-effort: if it fails we still refetch.
    void fetch('/metrics/cache/clear', { method: 'POST' }).catch(() => undefined)
    // removeQueries, not invalidateQueries: the latter respects staleTime, which
    // is a day.
    qc.removeQueries()
    setLoadedAt(new Date())
  }, [qc])

  const value = useMemo<Ctx>(
    () => ({ loaded: loadedAt !== null, loadedAt, load, refresh }),
    [loadedAt, load, refresh],
  )
  return <LoadCtx.Provider value={value}>{children}</LoadCtx.Provider>
}

export function useLoad(): Ctx {
  const ctx = useContext(LoadCtx)
  if (!ctx) throw new Error('useLoad must be used inside <LoadProvider>')
  return ctx
}
